import React from 'react';
import { Shield, Coins, Repeat, ShieldCheck, Server, Lock, Gauge, Sparkles } from 'lucide-react';
import { LOGO_URL } from '../lib/format';

export function HowItWorks() {
  const steps = [
    {
      icon: <Shield size={18} strokeWidth={1.6} />,
      title: 'Connect',
      body: 'Plug in Phantom or Solflare. No sign-up, no email — just cryptographic proof of you.',
    },
    {
      icon: <Coins size={18} strokeWidth={1.6} />,
      title: 'Deposit PUSD',
      body: 'Stake your stablecoins into the Palment vault program. A single atomic transaction.',
    },
    {
      icon: <Sparkles size={18} strokeWidth={1.6} />,
      title: 'Receive yPUSD',
      body: 'Mint yield-bearing receipt tokens. Their value grows with the vault, every block.',
    },
    {
      icon: <Repeat size={18} strokeWidth={1.6} />,
      title: 'Redeem anytime',
      body: 'Burn yPUSD to withdraw your original PUSD plus accrued yield — no lock-up.',
    },
  ];

  return (
    <section id="how" className="relative py-16 md:py-28">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="max-w-2xl">
          <div className="text-[11px] tracking-[0.26em] uppercase text-palm-mint/70">
            Protocol flow
          </div>
          <h2 className="mt-2 font-display font-bold tracking-tight text-3xl md:text-5xl">
            Four clicks between you and{' '}
            <span className="text-gradient">on-chain yield.</span>
          </h2>
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((s, i) => (
            <div
              key={s.title}
              className="relative glass rounded-2xl p-6 hover:-translate-y-1 hover:border-palm-mint/25 transition-all"
              data-testid={`how-step-${i + 1}`}
            >
              <div className="absolute -top-3 left-6 px-2.5 py-1 rounded-full bg-palm-surface border border-palm-lime/30 text-[10px] tracking-[0.24em] uppercase text-palm-lime font-bold">
                0{i + 1}
              </div>
              <div className="w-11 h-11 rounded-xl bg-palm-surface/60 border border-palm-mint/10 flex items-center justify-center text-palm-lime">
                {s.icon}
              </div>
              <div className="mt-4 font-display font-bold text-xl">{s.title}</div>
              <div className="mt-2 text-sm text-white/65 leading-relaxed">{s.body}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function SecurityStrip() {
  const items = [
    {
      icon: <ShieldCheck size={18} strokeWidth={1.6} />,
      title: 'Non-custodial',
      body: 'Funds live in a PDA governed by immutable program logic. No admin keys, no pauses.',
    },
    {
      icon: <Server size={18} strokeWidth={1.6} />,
      title: 'Censorship-resistant',
      body: 'Optimised RPC routing for high landing rates — even during Solana network congestion.',
    },
    {
      icon: <Gauge size={18} strokeWidth={1.6} />,
      title: 'Audited Anchor code',
      body: 'Built with modern Anchor (init_if_needed, strict PDAs) and open-sourced from day one.',
    },
    {
      icon: <Lock size={18} strokeWidth={1.6} />,
      title: 'Receipt tokens (yPUSD)',
      body: 'Composable across the Solana DeFi stack — use as collateral, LP, or simple store of yield.',
    },
  ];

  return (
    <section id="security" className="relative py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div
          className="relative rounded-3xl glass-strong p-8 md:p-12 overflow-hidden"
          data-testid="security-panel"
        >
          <div
            aria-hidden
            className="absolute -top-24 -left-24 w-96 h-96 rounded-full opacity-40 blur-3xl"
            style={{ background: 'radial-gradient(circle, #076653, transparent 65%)' }}
          />
          <div className="relative grid lg:grid-cols-5 gap-10 items-start">
            <div className="lg:col-span-2">
              <div className="text-[11px] tracking-[0.26em] uppercase text-palm-mint/70">
                Architecture
              </div>
              <h2 className="mt-2 font-display font-bold text-3xl md:text-4xl tracking-tight">
                Built for the{' '}
                <span className="text-gradient">unstoppable economy.</span>
              </h2>
              <p className="mt-5 text-white/65 leading-relaxed max-w-md">
                Palment is designed as pure infrastructure. No middlemen, no whitelists, no ability for anyone — including us — to freeze your position.
              </p>
              <div className="mt-8 flex items-center gap-3">
                <img src={LOGO_URL} alt="logo" className="w-10 h-10" />
                <div>
                  <div className="font-display font-bold tracking-[0.18em] text-sm">
                    PALMENT
                  </div>
                  <div className="text-[11px] tracking-[0.22em] uppercase text-palm-mint/60">
                    palment.xyz · devnet
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {items.map((it) => (
                <div
                  key={it.title}
                  className="rounded-2xl bg-black/20 border border-white/5 p-5 hover:border-palm-mint/20 transition-all"
                >
                  <div className="w-9 h-9 rounded-xl glass flex items-center justify-center text-palm-lime">
                    {it.icon}
                  </div>
                  <div className="mt-3 font-display font-semibold tracking-tight text-white/95">
                    {it.title}
                  </div>
                  <div className="mt-1.5 text-sm text-white/60 leading-relaxed">
                    {it.body}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Ticker() {
  const items = [
    'PALMENT',
    'YIELD · BEARING · VAULT',
    'PUSD → yPUSD',
    'SOLANA',
    'CENSORSHIP-RESISTANT',
    'NON-CUSTODIAL',
    'DEVNET',
    'APY ACCRUES EVERY BLOCK',
  ];
  const doubled = [...items, ...items];
  return (
    <div className="relative py-6 border-y border-white/5 bg-[#041411]/60 marquee overflow-hidden">
      <div className="marquee-track text-palm-mint/70 text-[11px] tracking-[0.4em] uppercase font-semibold">
        {doubled.map((t, i) => (
          <span key={`${t}-${i < items.length ? 'a' : 'b'}`} className="flex items-center gap-12 shrink-0">
            {t}
            <span className="w-1 h-1 rounded-full bg-palm-lime/50" />
          </span>
        ))}
      </div>
    </div>
  );
}

export function Footer() {
  return (
    <footer className="relative mt-10 pt-10 pb-12 border-t border-white/5">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="PALMENT" className="w-10 h-10" />
          <div>
            <div className="font-display font-black tracking-[0.18em] text-[13px]">
              PALMENT
            </div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-white/40">
              Censorship-resistant yield · Solana
            </div>
          </div>
        </div>
        <div className="text-xs text-white/40">
          © {new Date().getFullYear()} Palment Labs. Demo UI — devnet simulation.
        </div>
      </div>
    </footer>
  );
}
